import { CalendarClock, Clock, CornerDownRight, User } from "lucide-react"
import { comoFecha, hoyLocal, instanteDe } from "@/lib/fechas"

/**
 * Tarjeta de una tarea en el tablero.
 *
 * ⚠ **El selector «Mover a» no es un atajo: es el requisito.** WCAG 2.5.7 exige
 * que toda operación de arrastre tenga una alternativa de un solo puntero, y en un
 * tablero por columnas el arrastre es la interacción obvia. Se construyó primero la
 * alternativa y **después** el arrastre, que es el orden que hace que la alternativa
 * exista: al revés nunca llega. El `<select>` sigue siendo el camino completo —
 * alcanzable por teclado y por lector de pantalla—, y el arrastre es el añadido.
 *
 * ⚠ **El arrastre es nativo, sin librería.** Un tablero de cinco columnas no
 * justifica una dependencia con su propio modelo de accesibilidad que habría que
 * conciliar con el `<select>`. `draggable` más `dataTransfer` bastan, y lo que se
 * transporta es el identificador: arrastrar no debe poder mover otra cosa que la
 * tarjeta que se agarró.
 *
 * `aria-label` incluye el código de la tarea porque un tablero tiene decenas de
 * estos controles y «Mover a» a secas, repetido treinta veces, no le dice nada a
 * quien navega por lista de elementos.
 */

export type EstadoTarea = "PENDIENTE" | "EN_CURSO" | "EN_REVISION" | "HECHA" | "CANCELADA"
export type PrioridadTarea = "BAJA" | "MEDIA" | "ALTA" | "URGENTE"
export type TipoTarea = "OPERATIVA" | "ADMINISTRATIVA" | "DEV"

/** Los tres tipos, con el nombre que usa la gente y no el del enum. */
export const TIPOS: { valor: TipoTarea; etiqueta: string }[] = [
  { valor: "OPERATIVA", etiqueta: "Operativa" },
  { valor: "ADMINISTRATIVA", etiqueta: "Administrativa" },
  // Seguimiento del desarrollo del propio sistema. Es un tipo mas, no un tablero
  // aparte: quien coordina mira una sola pantalla.
  { valor: "DEV", etiqueta: "Desarrollo" },
]

export const PRIORIDADES: PrioridadTarea[] = ["BAJA", "MEDIA", "ALTA", "URGENTE"]

export interface Tarea {
  id: number
  codigo: string
  titulo: string
  tipo: TipoTarea
  estado: EstadoTarea
  prioridad: PrioridadTarea
  asignado_a: number | null
  vence_en: string | null
  cerrado_en: string | null
  creado_en: string
  /** Presente cuando es subtarea de otra. La jerarquía es de dos niveles. */
  tarea_padre_id: number | null
}

export const ESTADOS: EstadoTarea[] = ["PENDIENTE", "EN_CURSO", "EN_REVISION", "HECHA", "CANCELADA"]

/**
 * Días que lleva viva una tarea abierta.
 *
 * ⚠ Es la señal que un tablero no da solo: una tarjeta parada tres semanas se ve
 * exactamente igual que una de ayer, y por eso las columnas se llenan de trabajo que
 * nadie decidió abandonar — simplemente dejó de mirarse. Sólo cuenta hasta el cierre:
 * una tarea HECHA hace un mes no lleva un mes parada, lleva un mes hecha.
 */
export function diasDeVida(
  tarea: Pick<Tarea, "creado_en" | "cerrado_en">,
  hoy = new Date(),
): number {
  if (tarea.cerrado_en !== null) return 0
  const desde = instanteDe(tarea.creado_en)
  const ms = hoy.getTime() - desde.getTime()
  return Math.max(0, Math.floor(ms / 86_400_000))
}

/** A partir de aquí la antigüedad se enseña. Antes es ruido: casi todo dura días. */
const DIAS_PARA_AVISAR = 7

/** Sólo `URGENTE` y `ALTA` se colorean: si todo destaca, nada destaca. */
const COLOR_PRIORIDAD: Record<PrioridadTarea, string> = {
  URGENTE: "var(--destructive)",
  ALTA: "var(--warning)",
  MEDIA: "var(--fg-muted)",
  BAJA: "var(--fg-muted)",
}

export function TarjetaTarea({
  tarea,
  onMover,
  puedeMover,
  nombreAsignado,
  onAbrir,
  onArrastrar,
  seleccionada = false,
  codigoPadre,
}: {
  tarea: Tarea
  onMover: (estado: EstadoTarea) => void
  puedeMover: boolean
  /**
   * Nombre de quien la tiene. Lo resuelve la pantalla contra
   * `/usuarios/asignables`; si no llega, se cae a «Usuario N», que al menos no
   * miente. Un identificador numerico no le dice nada a nadie en un tablero.
   */
  nombreAsignado?: string
  onAbrir?: () => void
  /** Ausente en las vistas sin arrastre: entonces la tarjeta no es arrastrable. */
  onArrastrar?: () => void
  /**
   * La que tiene la ficha abierta. Se marca con el borde y con `aria-current`, no
   * solo con color: la ficha aparece a la derecha y sin esto no hay forma de saber
   * de cual de las quince tarjetas esta hablando.
   */
  seleccionada?: boolean
  /**
   * El código de la macro, cuando esta tarea cuelga de una. Lo resuelve la pantalla
   * contra las tareas que ya tiene cargadas: la tarjeta sólo trae `tarea_padre_id`,
   * y un número no le dice a nadie de qué depende.
   */
  codigoPadre?: string
}) {
  const vencida =
    tarea.vence_en !== null && tarea.cerrado_en === null && tarea.vence_en < hoyLocal()
  const dias = diasDeVida(tarea)

  return (
    <article
      draggable={onArrastrar !== undefined}
      onDragStart={(e) => {
        // El identificador, no el índice: una tarjeta filtrada fuera de la vista
        // cambiaría de posición y el índice movería la equivocada.
        e.dataTransfer.setData("text/plain", String(tarea.id))
        e.dataTransfer.effectAllowed = "move"
        onArrastrar?.()
      }}
      data-testid={`tarjeta-${tarea.id}`}
      aria-current={seleccionada ? true : undefined}
      className="relative superficie p-3"
      style={{
        borderColor: seleccionada ? "var(--primary-texto)" : "var(--cristal-filo)",
        cursor: onArrastrar === undefined ? undefined : "grab",
      }}
    >
      {/* ⚠ Antes del código y del título: sin esto, una subtarea y su macro se leen
          como dos encargos sueltos en el tablero —pasó— y quien coordina reparte dos
          veces algo que ya tenía dueño. */}
      {tarea.tarea_padre_id !== null && (
        <p
          data-testid={`cuelga-de-${tarea.id}`}
          className="mb-1 flex items-center gap-1 text-xs"
          style={{ color: "var(--fg-muted)" }}
        >
          <CornerDownRight size={12} aria-hidden="true" />
          Subtarea de <span className="tabular">{codigoPadre ?? "otra tarea"}</span>
        </p>
      )}

      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="tabular text-xs font-medium" style={{ color: "var(--fg-muted)" }}>
          {tarea.codigo}
        </span>
        <span className="text-xs font-medium" style={{ color: COLOR_PRIORIDAD[tarea.prioridad] }}>
          {tarea.prioridad}
        </span>
      </div>

      {/*
        El titulo ES el control de apertura, y por eso es un `<button>` y no un
        `<div>` con `onClick`: un div no recibe foco, no responde a Enter y un lector
        de pantalla no lo anuncia como algo que se pueda pulsar. Sin `onAbrir` se
        pinta como texto, que es lo correcto cuando no hay detalle que abrir.

        ⚠ **El area pulsable es la TARJETA ENTERA**, y se consigue estirando el
        pseudo-elemento del boton sobre ella (`after:absolute after:inset-0`). Es lo
        que la gente intenta: se pulsa la tarjeta, no las tres palabras del titulo.
        La alternativa —un `onClick` en el `<article>`— dejaria dos controles con el
        mismo destino, uno de ellos invisible para el teclado, y obligaria a filtrar
        a mano los clics que vienen del selector de estado. Aqui el selector se pone
        por encima con `relative z-10` y ya esta.
      */}
      {onAbrir ? (
        <button
          type="button"
          onClick={onAbrir}
          data-testid={`abrir-${tarea.id}`}
          className="block w-full text-left text-sm font-medium underline-offset-2 after:absolute after:inset-0 after:content-[''] hover:underline"
          style={{ color: "var(--fg)" }}
        >
          {tarea.titulo}
        </button>
      ) : (
        <p className="text-sm font-medium">{tarea.titulo}</p>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
        <span className="flex items-center gap-1" style={{ color: "var(--fg-muted)" }}>
          <User size={14} aria-hidden="true" />
          {tarea.asignado_a === null
            ? "Sin asignar"
            : (nombreAsignado ?? `Usuario ${tarea.asignado_a}`)}
        </span>
        {dias >= DIAS_PARA_AVISAR && (
          <span
            className="tabular flex items-center gap-1"
            data-testid={`antiguedad-${tarea.id}`}
            style={{ color: "var(--fg-muted)" }}
          >
            <Clock size={14} aria-hidden="true" />
            {dias} d
          </span>
        )}
        {tarea.vence_en && (
          <span
            className="tabular flex items-center gap-1"
            style={{ color: vencida ? "var(--destructive)" : "var(--fg-muted)" }}
          >
            <CalendarClock size={14} aria-hidden="true" />
            {comoFecha(tarea.vence_en)}
            {vencida ? " · vencida" : ""}
          </span>
        )}
      </div>

      {puedeMover && (
        // `relative z-10`: por encima de la capa que hace pulsable la tarjeta entera.
        // Sin esto, elegir un estado abriria la ficha en vez de mover la tarea.
        <div className="relative z-10 mt-3">
          <label
            htmlFor={`mover-${tarea.id}`}
            className="mb-1 block text-xs"
            style={{ color: "var(--fg-muted)" }}
          >
            Mover a
          </label>
          <select
            id={`mover-${tarea.id}`}
            aria-label={`Mover a otro estado la tarea ${tarea.codigo}`}
            value={tarea.estado}
            onChange={(e) => onMover(e.target.value as EstadoTarea)}
            className="h-8 w-full rounded-[var(--radius-control)] border px-2 text-xs"
            style={{
              borderColor: "var(--border)",
              backgroundColor: "var(--background)",
              color: "var(--fg)",
            }}
          >
            {ESTADOS.map((e) => (
              <option key={e} value={e}>
                {e.replaceAll("_", " ")}
              </option>
            ))}
          </select>
        </div>
      )}
    </article>
  )
}
